import React from 'react';
import { UserIcon, PaperAirplaneIcon, StopIcon, InboxIcon } from '@heroicons/react/24/outline';
import { Placeholder, TypingIndicator } from '../../../ui';
import ChatMessageContent from './ChatMessageContent';
import { deduplicateMessages, formatMessageTime } from '../utils/chatMessageUtils';

const AssistantChatPane = ({
  t,
  selectedSessionId,
  sessionSwitchLoading,
  chatLoading,
  messages,
  isProcessing,
  isOwner,
  input,
  textareaHeight,
  textAreaRef,
  messagesEndRef,
  messagesContainerRef,
  listLoading,
  isInitializing,
  onUserScroll,
  onSourceClick,
  onInputChange,
  onKeyPress,
  onSendMessage,
  onStopGeneration
}) => (
  <div className="flex-1 flex flex-col bg-white overflow-hidden min-h-0 relative">
    {sessionSwitchLoading && messages.length === 0 && (
      <div className="absolute inset-0 z-20 flex items-center justify-center bg-white/80">
        <Placeholder size="m" className="border-0 shadow-none bg-transparent" />
      </div>
    )}
    {selectedSessionId ? (
      <>
        <div
          ref={messagesContainerRef}
          className="flex-1 overflow-y-auto assistant-scrollbar py-4 px-6 messages-container"
          onScroll={onUserScroll}
          style={{
            scrollBehavior: 'smooth',
            scrollSnapType: 'y proximity',
            scrollPaddingTop: '8px'
          }}
        >
          {chatLoading && messages.length === 0 ? (
            <Placeholder size="l" className="border-0 shadow-none" />
          ) : (
            <div className="max-w-4xl mx-auto">
              {deduplicateMessages(messages).map((message, index) => (
                <div
                  key={index}
                  className={`flex ${message.sender === 'user' ? 'justify-end' : 'justify-start'} mb-4 message-item`}
                  style={{ scrollSnapAlign: 'start' }}
                >
                  <div className={`flex items-start ${message.sender === 'user' ? 'flex-row-reverse' : ''} w-full`}>
                    {message.sender === 'user' && (
                      <div className="flex-shrink-0 mr-4 mt-4 ml-4">
                        <div className="w-8 h-8 rounded-full bg-neutral-100 flex items-center justify-center">
                          <UserIcon className="w-5 h-5 text-neutral-500" />
                        </div>
                      </div>
                    )}
                    <div className={`chat-bubble ${message.sender === 'user'
                      ? 'mr-2 bg-neutral-100 text-neutral-900 rounded-tr-sm max-w-3xl'
                      : 'rounded-tl-sm w-full max-w-full'
                      }`}>
                      <div className="text-sm font-normal leading-relaxed prose prose-sm max-w-none">
                        <ChatMessageContent message={message} onSourceClick={onSourceClick} />
                      </div>
                      {formatMessageTime(message.date_created) && (
                        <div className={`mt-1 text-[11px] ${message.sender === 'user' ? 'text-gray-500 text-right' : 'text-gray-400 text-right'}`}>
                          {formatMessageTime(message.date_created)}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
              {isProcessing && (
                <div className="mb-2 w-full">
                  <div className="flex items-start w-full">
                    <div className="flex-grow">
                      <TypingIndicator />
                    </div>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>
          )}
        </div>

        {isOwner && (
          <div className="border-t border-gray-200 p-4">
            <div className="max-w-4xl mx-auto">
              <div className="relative">
                <textarea
                  ref={textAreaRef}
                  value={input}
                  onChange={onInputChange}
                  onKeyPress={onKeyPress}
                  style={{
                    height: textareaHeight,
                    maxHeight: '300px',
                    overflowY: 'auto',
                    paddingRight: '60px',
                    lineHeight: '24px',
                    minHeight: '56px',
                    backgroundColor: 'white'
                  }}
                  className="shadow-none ring-0 w-full py-4 px-4 resize-none bg-white border border-gray-300 rounded-lg focus:border-primary-500 focus:ring-1 focus:ring-primary-500 focus:outline-none disabled:bg-gray-50"
                  placeholder={t('assistant.typeMessage')}
                  rows={1}
                  disabled={isProcessing}
                />
                {isProcessing ? (
                  <button
                    type="button"
                    onClick={onStopGeneration}
                    className="absolute right-2 top-1/2 -translate-y-1/2 bg-gray-600 flex text-white p-2 rounded-full hover:bg-gray-900 focus:outline-none focus:ring-2 focus:ring-gray-600 focus:ring-opacity-50 active:bg-gray-700 border border-gray-600 transition duration-150 ease-in-out"
                  >
                    <StopIcon className="w-5 h-5" />
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={onSendMessage}
                    className={`absolute right-2 top-1/2 -translate-y-1/2 bg-primary-500 flex text-white p-2 rounded-full hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-opacity-50 active:bg-primary-700 border border-primary-500 transition duration-150 ease-in-out disabled:opacity-50 disabled:cursor-not-allowed ${!input.trim() || isProcessing ? 'bg-gray-400 cursor-not-allowed' : ''}`}
                    disabled={chatLoading || !input.trim() || isProcessing}
                  >
                    {chatLoading ? 'Waiting...' : <PaperAirplaneIcon className="w-5 h-5" />}
                  </button>
                )}
              </div>
              <p className="text-xs text-gray-500 mt-3 text-center">
                {t('assistant.aiDisclaimer')}
              </p>
            </div>
          </div>
        )}
      </>
    ) : listLoading || isInitializing ? (
      <Placeholder size="l" className="flex-1 border-0 shadow-none rounded-none" />
    ) : (
      <Placeholder
        empty
        size="l"
        icon={InboxIcon}
        message={t('assistant.startConversation')}
        description={t('assistant.inbox.emptyDescription')}
        className="flex-1"
      />
    )}
  </div>
);

export default AssistantChatPane;
